import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Fingerprint, Users, CalendarCheck, Clock, Wifi, WifiOff,
  CheckCircle, XCircle, Trash2, AlertCircle,
  RefreshCw, Search, GraduationCap, Filter,
} from 'lucide-react';
import { biometricAPI } from '../services/api';
import { useYear } from '../context/YearContext';

const TABS = [
  { id: 'students',    label: 'Student Verification', icon: GraduationCap },
  { id: 'student-log', label: 'Attendance Log',        icon: CalendarCheck },
];

const fmt = (t) => {
  if (!t) return '—';
  const [h, m] = t.split(':');
  const hr = parseInt(h, 10);
  return `${hr % 12 || 12}:${m} ${hr < 12 ? 'AM' : 'PM'}`;
};

const today = () => new Date().toISOString().slice(0, 10);

function PunchBadge({ type }) {
  const isIn = type === 'IN';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11,
      fontWeight: 700, padding: '3px 10px', borderRadius: 12,
      background: isIn ? '#dbeafe' : '#fef3c7',
      color: isIn ? '#1e40af' : '#92400e',
      letterSpacing: '0.5px',
    }}>
      {isIn ? '→ IN' : '← OUT'}
    </span>
  );
}

// ── Student Verification Tab ──────────────────────────────────────────────────
function StudentVerification({ classes, year, summary, onPunchRecorded }) {
  const [selectedClass, setSelectedClass] = useState('');
  const [students, setStudents]           = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [deviceStatus, setDeviceStatus]   = useState('disconnected');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [punchType, setPunchType]         = useState('IN');
  const [deviceId, setDeviceId]           = useState('BIO-DEV-01');
  const [scanProgress, setScanProgress]   = useState(0);
  const [lastPunch, setLastPunch]         = useState(null);
  const [search, setSearch]               = useState('');
  const [err, setErr]                     = useState('');
  const scanTimer = useRef(null);

  useEffect(() => { setSelectedClass(''); setStudents([]); setSelectedStudent(null); }, [year]);

  const classSummary = selectedClass ? (summary.find((r) => r.class === selectedClass) || { total: 0, present: 0, absent: 0 }) : null;

  useEffect(() => {
    if (!selectedClass) { setStudents([]); setSelectedStudent(null); return; }
    setLoadingStudents(true);
    setSelectedStudent(null);
    biometricAPI.getStudentsByClass({ class: selectedClass, year })
      .then((res) => setStudents(res.data?.data || []))
      .catch(() => setStudents([]))
      .finally(() => setLoadingStudents(false));
  }, [selectedClass, year]);

  const filtered = students.filter((s) =>
    s.student_name.toLowerCase().includes(search.toLowerCase()) ||
    (s.admission_no || '').toLowerCase().includes(search.toLowerCase())
  );

  const startScan = () => {
    if (!selectedStudent) { setErr('Select a student first'); return; }
    setErr(''); setDeviceStatus('scanning'); setScanProgress(0);
    let progress = 0;
    scanTimer.current = setInterval(() => {
      progress += 8;
      setScanProgress(Math.min(progress, 100));
      if (progress >= 100) { clearInterval(scanTimer.current); recordPunch(); }
    }, 120);
  };

  const recordPunch = async () => {
    try {
      const now = new Date();
      await biometricAPI.punchStudentAttendance({
        student_id: selectedStudent.student_id,
        punch_date: now.toISOString().slice(0, 10),
        punch_time: now.toTimeString().slice(0, 8),
        punch_type: punchType,
        device_id:  deviceId || 'LOCAL',
      });
      setDeviceStatus('verified');
      setLastPunch({ student: selectedStudent, type: punchType, time: now.toTimeString().slice(0, 5) });
      onPunchRecorded();
      setTimeout(() => { setDeviceStatus('disconnected'); setScanProgress(0); }, 3500);
    } catch (e) {
      setDeviceStatus('failed');
      setErr(e.response?.data?.message || 'Punch failed');
      setTimeout(() => { setDeviceStatus('disconnected'); setScanProgress(0); }, 2500);
    }
  };

  useEffect(() => () => clearInterval(scanTimer.current), []);

  const statusColors = {
    disconnected: { bg: '#f8fafc', border: '#e2e8f0', icon: '#94a3b8' },
    scanning:     { bg: '#eff6ff', border: '#93c5fd', icon: '#3b82f6' },
    verified:     { bg: '#f0fdf4', border: '#86efac', icon: '#16a34a' },
    failed:       { bg: '#fef2f2', border: '#fca5a5', icon: '#dc2626' },
  };
  const sc = statusColors[deviceStatus];

  return (
    <div>
      {classSummary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 20 }}>
          {[
            { label: `Total in ${selectedClass}`, value: classSummary.total,   icon: GraduationCap, color: '#6366f1', bg: '#eef2ff' },
            { label: 'Present Today',              value: classSummary.present, icon: CheckCircle,   color: '#16a34a', bg: '#f0fdf4' },
            { label: 'Absent Today',               value: classSummary.absent,  icon: XCircle,       color: '#dc2626', bg: '#fef2f2' },
          ].map((c) => (
            <div key={c.label} style={{ background: c.bg, borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, border: '1px solid rgba(0,0,0,0.05)' }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                <c.icon size={17} color={c.color} />
              </div>
              <div>
                <div style={{ fontSize: 20, fontWeight: 800, color: c.color, lineHeight: 1 }}>{c.value}</div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{c.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 20, alignItems: 'start' }}>
        {/* Left: class filter + student selector */}
        <div>
          <div style={{ marginBottom: 14, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f1f5f9', borderRadius: 9, padding: '6px 12px' }}>
              <Filter size={13} color="#6366f1" />
              <span style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Class:</span>
              <select
                value={selectedClass}
                onChange={(e) => { setSelectedClass(e.target.value); setSearch(''); }}
                style={{ border: 'none', background: 'transparent', fontSize: 13, fontWeight: 700, color: '#1e293b', cursor: 'pointer', outline: 'none', minWidth: 100 }}
              >
                <option value="">-- Select Class --</option>
                {classes.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            {selectedClass && (
              <div style={{ position: 'relative', flex: 1, minWidth: 160 }}>
                <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                <input
                  value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name or admission no…"
                  style={{ width: '100%', padding: '8px 10px 8px 32px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit' }}
                />
              </div>
            )}

            <select value={punchType} onChange={(e) => setPunchType(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, fontWeight: 700, background: punchType === 'IN' ? '#dbeafe' : '#fef3c7', color: punchType === 'IN' ? '#1e40af' : '#92400e', cursor: 'pointer' }}>
              <option value="IN">→ IN</option>
              <option value="OUT">← OUT</option>
            </select>
          </div>

          {!selectedClass ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 13 }}>
              <GraduationCap size={36} style={{ opacity: 0.25, display: 'block', margin: '0 auto 12px' }} />
              Select a class above to load students
            </div>
          ) : loadingStudents ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 13 }}>Loading students…</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10, maxHeight: 420, overflowY: 'auto' }}>
              {filtered.map((s) => {
                const sel = selectedStudent?.student_id === s.student_id;
                return (
                  <div key={s.student_id} onClick={() => { setSelectedStudent(s); setErr(''); }}
                    style={{
                      padding: '11px 13px', borderRadius: 10, cursor: 'pointer', transition: 'all 0.15s',
                      border: sel ? '2px solid #6366f1' : '1.5px solid #e2e8f0',
                      background: sel ? '#eef2ff' : '#fff',
                      boxShadow: sel ? '0 0 0 3px rgba(99,102,241,0.12)' : 'none',
                    }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                      <div style={{ width: 34, height: 34, borderRadius: 9, background: sel ? '#6366f1' : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <GraduationCap size={15} color={sel ? '#fff' : '#94a3b8'} />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.student_name}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>{s.father_name || '—'}</div>
                        {s.admission_no && <div style={{ fontSize: 10, color: '#6366f1', fontWeight: 600 }}>Adm: {s.admission_no}</div>}
                      </div>
                    </div>
                  </div>
                );
              })}
              {filtered.length === 0 && (
                <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: 30, color: '#94a3b8', fontSize: 13 }}>
                  No students found in {selectedClass}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right: scanner panel */}
        <div>
          <div style={{ background: sc.bg, border: `2px solid ${sc.border}`, borderRadius: 16, padding: 24, textAlign: 'center', transition: 'all 0.3s' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 18 }}>
              {deviceStatus === 'disconnected' ? <WifiOff size={14} color="#94a3b8" /> : <Wifi size={14} color="#3b82f6" />}
              <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>
                {deviceStatus === 'disconnected' ? 'Device Ready' :
                 deviceStatus === 'scanning' ? 'Scanning…' :
                 deviceStatus === 'verified' ? 'Verified!' : 'Scan Failed'}
              </span>
            </div>

            <div style={{ position: 'relative', display: 'inline-block', marginBottom: 18 }}>
              <div style={{
                width: 100, height: 100, borderRadius: '50%', margin: '0 auto',
                background: sc.bg, border: `3px solid ${sc.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                position: 'relative', overflow: 'hidden',
              }}>
                <Fingerprint size={48} color={sc.icon} strokeWidth={1.5} />
                {deviceStatus === 'scanning' && (
                  <div style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    height: `${scanProgress}%`, background: 'rgba(59,130,246,0.15)',
                    transition: 'height 0.1s linear',
                  }} />
                )}
              </div>
              {deviceStatus === 'verified' && (
                <div style={{ position: 'absolute', top: -4, right: -4, width: 28, height: 28, borderRadius: '50%', background: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CheckCircle size={16} color="#fff" />
                </div>
              )}
              {deviceStatus === 'failed' && (
                <div style={{ position: 'absolute', top: -4, right: -4, width: 28, height: 28, borderRadius: '50%', background: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <XCircle size={16} color="#fff" />
                </div>
              )}
            </div>

            {selectedStudent ? (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>{selectedStudent.student_name}</div>
                <div style={{ fontSize: 12, color: '#64748b' }}>{selectedStudent.class} · {selectedStudent.father_name}</div>
                {selectedStudent.admission_no && <div style={{ fontSize: 11, color: '#6366f1', fontWeight: 600 }}>Adm: {selectedStudent.admission_no}</div>}
              </div>
            ) : (
              <div style={{ marginBottom: 16, color: '#94a3b8', fontSize: 13 }}>Select a student from the list</div>
            )}

            <input value={deviceId} onChange={(e) => setDeviceId(e.target.value)}
              placeholder="Device ID (e.g. BIO-DEV-01)"
              style={{ width: '100%', padding: '7px 10px', borderRadius: 7, border: '1.5px solid #e2e8f0', fontSize: 12, marginBottom: 12, boxSizing: 'border-box', textAlign: 'center', fontFamily: 'inherit' }} />

            {err && (
              <div style={{ marginBottom: 10, padding: '8px 12px', background: '#fef2f2', borderRadius: 7, color: '#dc2626', fontSize: 12, display: 'flex', gap: 6, alignItems: 'center' }}>
                <AlertCircle size={13} /> {err}
              </div>
            )}

            <button onClick={startScan} disabled={deviceStatus === 'scanning' || !selectedStudent}
              style={{
                width: '100%', padding: '11px', borderRadius: 10, border: 'none',
                cursor: (deviceStatus === 'scanning' || !selectedStudent) ? 'not-allowed' : 'pointer',
                background: deviceStatus === 'verified' ? '#16a34a' : deviceStatus === 'failed' ? '#dc2626' : '#6366f1',
                color: '#fff', fontWeight: 700, fontSize: 14,
                opacity: (deviceStatus === 'scanning' || !selectedStudent) ? 0.7 : 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}>
              <Fingerprint size={16} />
              {deviceStatus === 'scanning' ? `Scanning… ${scanProgress}%` :
               deviceStatus === 'verified' ? 'Verified!' :
               deviceStatus === 'failed' ? 'Try Again' :
               `Mark ${punchType === 'IN' ? 'Punch-In' : 'Punch-Out'}`}
            </button>
          </div>

          {lastPunch && (
            <div style={{ marginTop: 14, padding: '12px 16px', background: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: 12 }}>
              <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, marginBottom: 4 }}>LAST PUNCH</div>
              <div style={{ fontWeight: 700, color: '#065f46', fontSize: 14 }}>{lastPunch.student.student_name}</div>
              <div style={{ fontSize: 12, color: '#059669', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Clock size={12} /> {lastPunch.type === 'IN' ? 'Punched In' : 'Punched Out'} at {lastPunch.time}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function StudentBiometric() {
  const { year } = useYear();
  const [activeTab, setActiveTab] = useState('students');
  const [studentClasses, setStudentClasses]       = useState([]);
  const [studentLogClass, setStudentLogClass]     = useState('');
  const [studentLogDate, setStudentLogDate]       = useState(today);
  const [studentLogs, setStudentLogs]             = useState([]);
  const [studentLogLoading, setStudentLogLoading] = useState(false);
  const [studentSummary, setStudentSummary]       = useState([]);
  const [toast, setToast]                         = useState(null);

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const loadStudentClasses = useCallback(async () => {
    try {
      const res = await biometricAPI.getStudentClasses({ year });
      setStudentClasses(res.data?.data || []);
    } catch { /* silent */ }
  }, [year]);

  const loadStudentSummary = useCallback(async () => {
    try {
      const res = await biometricAPI.getStudentSummary({ year });
      setStudentSummary(res.data?.data || []);
    } catch { /* silent */ }
  }, [year]);

  const loadStudentLogs = useCallback(async (date, cls) => {
    setStudentLogLoading(true);
    try {
      const params = { date };
      if (cls) params.class = cls;
      const res = await biometricAPI.getStudentAttendance(params);
      setStudentLogs(res.data?.data || []);
    } catch {
      showToast('Failed to load student attendance', 'error');
    } finally {
      setStudentLogLoading(false);
    }
  }, [showToast]);

  useEffect(() => { loadStudentClasses(); loadStudentSummary(); }, [loadStudentClasses, loadStudentSummary, year]);
  useEffect(() => {
    if (activeTab === 'student-log') loadStudentLogs(studentLogDate, studentLogClass);
  }, [activeTab, studentLogDate, studentLogClass, loadStudentLogs]);

  const handleDeleteStudentLog = async (log) => {
    if (!window.confirm('Delete this student attendance record?')) return;
    try {
      await biometricAPI.deleteStudentLog(log.log_id);
      showToast('Record deleted');
      loadStudentLogs(studentLogDate, studentLogClass);
    } catch {
      showToast('Delete failed', 'error');
    }
  };

  const totalAll   = studentSummary.reduce((s, r) => s + r.total,   0);
  const presentAll = studentSummary.reduce((s, r) => s + r.present, 0);
  const absentAll  = studentSummary.reduce((s, r) => s + r.absent,  0);

  return (
    <div style={{ padding: '24px', fontFamily: 'inherit', maxWidth: 1200, margin: '0 auto' }}>
      {toast && createPortal(
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          padding: '12px 20px', borderRadius: 10, fontWeight: 600, fontSize: 13,
          background: toast.type === 'error' ? '#dc2626' : '#16a34a', color: '#fff',
          boxShadow: '0 4px 24px rgba(0,0,0,0.18)', display: 'flex', alignItems: 'center', gap: 8,
        }}>
          {toast.type === 'error' ? <XCircle size={15} /> : <CheckCircle size={15} />}
          {toast.msg}
        </div>,
        document.body
      )}

      {/* Page header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 6 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <GraduationCap size={22} color="#fff" />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#1e293b' }}>Student Biometric</h1>
            <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>Student attendance tracking by class</p>
          </div>
        </div>

        {/* Summary tiles */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginTop: 18 }}>
          {[
            { label: 'Total Students', value: totalAll,              icon: GraduationCap, color: '#6366f1', bg: '#eef2ff' },
            { label: 'Present Today',  value: presentAll,            icon: CheckCircle,   color: '#16a34a', bg: '#f0fdf4' },
            { label: 'Absent Today',   value: absentAll,             icon: XCircle,       color: '#dc2626', bg: '#fef2f2' },
            { label: 'Classes',        value: studentSummary.length, icon: Users,         color: '#0891b2', bg: '#ecfeff' },
          ].map((c) => (
            <div key={c.label} style={{ background: c.bg, borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, border: '1px solid rgba(0,0,0,0.05)' }}>
              <div style={{ width: 38, height: 38, borderRadius: 10, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                <c.icon size={18} color={c.color} />
              </div>
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: c.color, lineHeight: 1 }}>{c.value}</div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{c.label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: '#f1f5f9', borderRadius: 10, padding: 4, width: 'fit-content' }}>
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{
              padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600,
              background: activeTab === t.id ? '#fff' : 'transparent',
              color: activeTab === t.id ? '#6366f1' : '#64748b',
              boxShadow: activeTab === t.id ? '0 1px 6px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.15s',
            }}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {/* Student Verification */}
      {activeTab === 'students' && (
        <StudentVerification
          classes={studentClasses}
          year={year}
          summary={studentSummary}
          onPunchRecorded={() => { showToast('Student attendance recorded successfully'); loadStudentSummary(); }}
        />
      )}

      {/* Student Log */}
      {activeTab === 'student-log' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>Date:</label>
            <input type="date" value={studentLogDate} max={today()} lang="en-GB"
              onChange={(e) => setStudentLogDate(e.target.value)}
              style={{ padding: '7px 10px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, fontFamily: 'inherit' }} />

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f1f5f9', borderRadius: 9, padding: '6px 12px' }}>
              <Filter size={13} color="#6366f1" />
              <span style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Class:</span>
              <select
                value={studentLogClass}
                onChange={(e) => setStudentLogClass(e.target.value)}
                style={{ border: 'none', background: 'transparent', fontSize: 13, fontWeight: 700, color: '#1e293b', cursor: 'pointer', outline: 'none', minWidth: 100 }}
              >
                <option value="">All Classes</option>
                {studentClasses.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <button onClick={() => loadStudentLogs(studentLogDate, studentLogClass)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#475569' }}>
              <RefreshCw size={13} /> Refresh
            </button>
            {studentLogs.length > 0 && (
              <span style={{ marginLeft: 'auto', fontSize: 12, color: '#64748b' }}>
                {studentLogs.length} punch{studentLogs.length !== 1 ? 'es' : ''} recorded
              </span>
            )}
          </div>

          {studentLogLoading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Loading…</div>
          ) : (
            <div className="table-container" style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['Student Name', 'Father Name', 'Class', 'Adm No', 'Time', 'Punch', 'Device', ''].map((h) => (
                      <th key={h} style={{ padding: '11px 14px', textAlign: 'left', fontWeight: 700, color: '#475569', fontSize: 11, whiteSpace: 'nowrap', borderBottom: '1px solid #f1f5f9' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {studentLogs.length === 0 ? (
                    <tr>
                      <td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
                        <GraduationCap size={32} style={{ opacity: 0.3, display: 'block', margin: '0 auto 10px' }} />
                        No student attendance records for this date.
                        <div style={{ marginTop: 6, fontSize: 12 }}>Use <strong>Student Verification</strong> tab to mark attendance.</div>
                      </td>
                    </tr>
                  ) : studentLogs.map((log, i) => (
                    <tr key={log.log_id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ padding: '10px 14px', fontWeight: 700, color: '#1e293b' }}>{log.student_name}</td>
                      <td style={{ padding: '10px 14px', color: '#475569' }}>{log.father_name || '—'}</td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#eef2ff', color: '#6366f1', borderRadius: 6, padding: '2px 8px', fontWeight: 700, fontSize: 12 }}>
                          {log.class}
                        </span>
                      </td>
                      <td style={{ padding: '10px 14px', color: '#475569', fontFamily: 'monospace', fontSize: 12 }}>{log.admission_no || '—'}</td>
                      <td style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 5, color: '#1e293b', fontWeight: 600 }}>
                        <Clock size={13} color="#6366f1" /> {fmt(log.punch_time)}
                      </td>
                      <td style={{ padding: '10px 14px' }}><PunchBadge type={log.punch_type} /></td>
                      <td style={{ padding: '10px 14px', color: '#64748b', fontSize: 12, fontFamily: 'monospace' }}>{log.device_id}</td>
                      <td style={{ padding: '10px 14px' }}>
                        <button onClick={() => handleDeleteStudentLog(log)}
                          style={{ padding: '5px 7px', borderRadius: 6, border: '1px solid #fee2e2', background: '#fff', cursor: 'pointer', color: '#dc2626' }}
                          title="Delete record">
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
